import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, CalendarClock, Plus, X, Save, Send, Trash2, Mic2,
  ChevronLeft, ChevronRight, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PLATFORMS = ["instagram", "tiktok", "facebook", "x", "youtube", "linkedin"];
const STATUS_STYLES = {
  scheduled: "bg-[#7A8B6F]/25 text-[#F5EFE2] border-[#7A8B6F]/60",
  posted: "bg-[#C9A961]/25 text-[#F5EFE2] border-[#C9A961]/70",
  failed: "bg-[#B85A4A]/25 text-[#F5EFE2] border-[#B85A4A]/70",
  draft: "bg-[#5E594E]/60 text-[#C9BEA8] border-[#5E594E]",
};

function ymdKey(d) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}
function ymdKeyFromISO(iso) {
  return ymdKey(new Date(iso));
}
function toDatetimeLocal(iso) {
  return iso ? new Date(iso).toISOString().slice(0, 16) : "";
}

function GenerateModal({ open, onClose, onDone }) {
  const [days, setDays] = useState(14);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errors, setErrors] = useState([]);
  if (!open) return null;
  const start = async () => {
    setRunning(true);
    setProgress({ done: 0, total: days * PLATFORMS.length });
    setErrors([]);
    try {
      const res = await api.post("/admin/calendar/generate", { days, platforms: PLATFORMS }, { timeout: 15 * 60 * 1000 });
      setProgress({ done: res.data.generated_count, total: res.data.generated_count });
      setErrors(res.data.errors || []);
      toast.success(`Generated ${res.data.generated_count} drafts`);
      onDone();
    } catch (e) {
      toast.error("Generation failed", { description: readApiError(e) });
    } finally {
      setRunning(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="calendar-gen-modal" onClick={running ? undefined : onClose}>
      <div className="w-full max-w-md rounded-2xl border border-[#5E594E] bg-[#5A5347] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-serif text-2xl tracking-wide text-[#F5EFE2]">Generate calendar drafts</p>
          {!running && (
            <button onClick={onClose} className="text-[#9A8E78] hover:text-[#F5EFE2]" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="mt-2 text-sm text-[#C9BEA8]">
          One AI call per day × platform. Drafts land in the calendar — nothing schedules automatically.
        </p>
        <div className="mt-5 grid grid-cols-3 gap-2">
          {[7, 14, 30].map((n) => (
            <button
              key={n}
              type="button"
              disabled={running}
              onClick={() => setDays(n)}
              data-testid={`calendar-gen-days-${n}`}
              className={`rounded-lg border px-4 py-3 text-center transition-colors ${
                days === n ? "border-[#C9A961] bg-[#C9A961] text-[#3D3A33]" : "border-[#5E594E] bg-[#3D3A33] text-[#C9BEA8] hover:border-[#C9A961]/60"
              }`}
            >
              <p className="font-serif text-lg">{n} days</p>
              <p className="text-[10px] uppercase tracking-wider">{n * 6} drafts</p>
            </button>
          ))}
        </div>
        {running ? (
          <div className="mt-5">
            <div className="flex items-center gap-2 text-sm text-[#C9A961]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating {progress.total} drafts (real AI calls, ~1-2s each)…
            </div>
            <p className="mt-2 text-xs text-[#9A8E78]">Keep this tab open. This can take a couple of minutes.</p>
          </div>
        ) : (
          <div className="mt-5 flex gap-2">
            <Button
              onClick={start}
              data-testid="calendar-gen-start"
              className="flex-1 bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
            >
              <Sparkles className="h-4 w-4" />
              Generate {days * 6} drafts
            </Button>
            <Button variant="outline" onClick={onClose} className="border-[#5E594E] bg-transparent text-[#C9BEA8]">
              Cancel
            </Button>
          </div>
        )}
        {errors.length > 0 && (
          <div className="mt-3 text-xs text-[#B85A4A]">
            <p className="font-medium">Errors ({errors.length}):</p>
            <ul className="mt-1 space-y-0.5">
              {errors.slice(0, 5).map((e, i) => <li key={i} className="truncate">{e}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function EditDrawer({ post, onClose, onSaved, onDeleted, library }) {
  const [content, setContent] = useState(post?.content || "");
  const [scheduledAt, setScheduledAt] = useState(toDatetimeLocal(post?.scheduled_at));
  const [imageUrl, setImageUrl] = useState(post?.image_url || "");
  const [platforms, setPlatforms] = useState(post?.platforms || []);
  const [voiceClipId, setVoiceClipId] = useState(post?.voice_clip_id || null);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [picker, setPicker] = useState(false);
  if (!post) return null;

  const toggle = (p) => setPlatforms((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const save = async () => {
    setSaving(true);
    try {
      const iso = scheduledAt ? new Date(scheduledAt).toISOString() : post.scheduled_at;
      const res = await api.put(`/admin/calendar/posts/${post.id}`, {
        content, scheduled_at: iso, image_url: imageUrl, platforms,
      });
      toast.success("Saved");
      onSaved(res.data);
    } catch (e) {
      toast.error("Save failed", { description: readApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  const schedule = async () => {
    if (requiredWithoutMedia) {
      toast.error(`${requiredLabels} require an image`, {
        description: "Pick one from the library or unselect those platforms.",
      });
      return;
    }
    setScheduling(true);
    try {
      // Save first
      const iso = scheduledAt ? new Date(scheduledAt).toISOString() : post.scheduled_at;
      await api.put(`/admin/calendar/posts/${post.id}`, { content, scheduled_at: iso, image_url: imageUrl, platforms });
      await api.post(`/admin/calendar/posts/${post.id}/schedule`);
      toast.success("Scheduled to Ayrshare");
      onSaved({ ...post, status: "scheduled" });
    } catch (e) {
      const err = e?.response?.data?.detail;
      const errStr = typeof err === "string" ? err : JSON.stringify(err);
      // Ayrshare 4xx surfaced verbatim
      toast.error("Ayrshare rejected", { description: errStr?.slice(0, 500) });
    } finally {
      setScheduling(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/calendar/posts/${post.id}`);
      toast.success("Deleted");
      onDeleted(post.id);
    } catch (e) {
      toast.error("Delete failed", { description: readApiError(e) });
    }
  };

  const runVoice = async () => {
    setVoiceLoading(true);
    try {
      const res = await api.post(`/admin/calendar/posts/${post.id}/voiceover`);
      setVoiceClipId(res.data.clip_id);
      toast.success(`Voiceover: ${res.data.voice_name}`, { description: `~${Math.round(res.data.duration_estimate_sec || 0)}s clip saved to Library` });
    } catch (e) {
      toast.error("Voiceover failed", { description: readApiError(e) });
    } finally {
      setVoiceLoading(false);
    }
  };

  const platformCharLimit = (p) => ({ instagram: 2200, tiktok: 2200, facebook: 5000, x: 280, youtube: 5000, linkedin: 3000 }[p] || 2200);
  const overLimit = platforms.filter((p) => content.length > platformCharLimit(p));

  // Ayrshare error codes 439/139 — these platforms reject text-only posts.
  const MEDIA_REQUIRED = ["instagram", "tiktok", "youtube"];
  const requiredWithoutMedia = !imageUrl && platforms.some((p) => MEDIA_REQUIRED.includes(p));
  const requiredLabels = platforms
    .filter((p) => MEDIA_REQUIRED.includes(p))
    .map((p) => ({ instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube" }[p]))
    .join(" / ");

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="calendar-drawer" onClick={onClose}>
      <div className="flex-1 bg-black/60" />
      <div
        className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-[#5E594E] bg-[#5A5347]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between border-b border-[#5E594E] bg-[#5A5347] px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C9A961]">{post.status}</p>
            <p className="font-serif text-xl text-[#F5EFE2]">Edit post</p>
          </div>
          <button onClick={onClose} className="text-[#9A8E78] hover:text-[#F5EFE2]" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          {post.last_error && (
            <div className="rounded-lg border border-[#B85A4A]/60 bg-[#B85A4A]/10 p-3 text-xs text-[#B85A4A]">
              <p className="font-medium">Previous scheduling error (verbatim):</p>
              <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words">{post.last_error}</pre>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Copy</Label>
            <Textarea
              data-testid="drawer-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={8}
              className="mt-1 border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2]"
            />
            <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] text-[#9A8E78]">
              {platforms.map((p) => {
                const limit = platformCharLimit(p);
                const over = content.length > limit;
                return (
                  <span
                    key={p}
                    className={over ? "text-[#B85A4A]" : ""}
                  >
                    {p}: {content.length}/{limit}
                  </span>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Schedule (UTC)</Label>
            <Input
              data-testid="drawer-schedule"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="mt-1 border-[#5E594E] bg-[#3D3A33] text-[#F5EFE2]"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Platforms</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PLATFORMS.map((p) => {
                const on = platforms.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => toggle(p)}
                    data-testid={`drawer-platform-${p}`}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      on ? "border-[#C9A961] bg-[#C9A961] text-[#3D3A33]" : "border-[#5E594E] bg-[#3D3A33] text-[#C9BEA8]"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-[#9A8E78]">Image</Label>
            <div className="mt-2 flex items-center gap-3">
              {imageUrl ? (
                <img src={imageUrl} alt="" className="h-16 w-16 rounded border border-[#5E594E] object-cover" />
              ) : (
                <span className="text-xs text-[#9A8E78]">No image</span>
              )}
              <Button type="button" size="sm" variant="outline" onClick={() => setPicker((v) => !v)} className="border-[#5E594E] bg-transparent text-[#C9BEA8]">
                {picker ? "Close" : "Pick from library"}
              </Button>
              {imageUrl && (
                <button onClick={() => setImageUrl("")} className="text-xs text-[#B85A4A] hover:underline">Remove</button>
              )}
            </div>
            {picker && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded border border-[#5E594E] bg-[#3D3A33] p-2">
                <div className="grid grid-cols-5 gap-1.5">
                  {library.slice(0, 30).map((img) => (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => { setImageUrl(img.url); setPicker(false); }}
                      className={`overflow-hidden rounded border ${imageUrl === img.url ? "border-[#C9A961]" : "border-[#5E594E]"}`}
                    >
                      <img src={img.url} alt={img.name} className="aspect-square w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-lg border border-[#5E594E] bg-[#3D3A33] p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-[#F5EFE2]">
                <Mic2 className="h-4 w-4 text-[#C9A961]" />
                Voiceover
                {voiceClipId && (
                  <span className="rounded-full border border-[#7A8B6F]/60 px-2 py-0.5 text-[10px] text-[#7A8B6F]">
                    clip · {voiceClipId.slice(0, 8)}
                  </span>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={runVoice}
                disabled={voiceLoading || !content.trim()}
                data-testid="drawer-voice-btn"
                className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
              >
                {voiceLoading ? <><Loader2 className="h-3 w-3 animate-spin" />Recording…</> : "Generate Voiceover"}
              </Button>
            </div>
            <p className="mt-1 text-[10px] text-[#9A8E78]">
              Saves to `voice_clips` and links back to this post as `voice_clip_id`. Discoverable in Library.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-wrap gap-2 border-t border-[#5E594E] bg-[#5A5347] px-6 py-4">
          {requiredWithoutMedia && (
            <p
              data-testid="drawer-media-warning"
              className="mb-2 w-full rounded-md border border-[#B85A4A]/50 bg-[#B85A4A]/10 px-3 py-2 text-xs text-[#E8B4A6]"
            >
              <strong className="text-[#F5EFE2]">{requiredLabels}</strong> require an image. Pick one from
              the library or unselect those platforms.
            </p>
          )}
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="drawer-save"
            variant="outline"
            className="border-[#5E594E] bg-transparent text-[#C9BEA8] hover:bg-[#5E594E]"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
            Save draft
          </Button>
          <Button
            type="button"
            onClick={schedule}
            disabled={scheduling || overLimit.length > 0 || requiredWithoutMedia}
            data-testid="drawer-schedule-btn"
            className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            {scheduling ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Schedule
          </Button>
          <Button
            type="button"
            onClick={del}
            data-testid="drawer-delete"
            variant="outline"
            className="ml-auto border-[#B85A4A]/60 bg-transparent text-[#B85A4A] hover:bg-[#B85A4A]/10"
          >
            <Trash2 className="h-3 w-3" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCalendar() {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerPost, setDrawerPost] = useState(null);
  const [library, setLibrary] = useState([]);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), 1));
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/calendar/posts");
      setPosts(res.data.posts || []);
    } catch (e) {
      toast.error("Could not load calendar", { description: readApiError(e) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    api.get("/admin/library/images").then((r) => setLibrary(r.data.images || [])).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of posts) {
      const key = ymdKeyFromISO(p.scheduled_at);
      (g[key] = g[key] || []).push(p);
    }
    return g;
  }, [posts]);

  const monthMatrix = useMemo(() => {
    const y = cursor.getUTCFullYear();
    const m = cursor.getUTCMonth();
    const first = new Date(Date.UTC(y, m, 1));
    const start = new Date(first);
    start.setUTCDate(1 - first.getUTCDay()); // start on Sunday
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);
      cells.push(d);
    }
    return cells;
  }, [cursor]);

  const monthLabel = cursor.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  const isEmpty = !loading && posts.length === 0;

  return (
    <div className="space-y-8" data-testid="admin-calendar">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">Content Calendar</p>
          <h1 className="admin-h1 mt-2">
            The workbench schedule.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const d = new Date(cursor); d.setUTCMonth(cursor.getUTCMonth() - 1); setCursor(d);
            }}
            className="border-[#5E594E] bg-transparent text-[#C9BEA8]"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="font-serif text-lg text-[#F5EFE2]" data-testid="calendar-month-label">{monthLabel}</span>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const d = new Date(cursor); d.setUTCMonth(cursor.getUTCMonth() + 1); setCursor(d);
            }}
            className="border-[#5E594E] bg-transparent text-[#C9BEA8]"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="calendar-open-gen"
            className="bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            <Plus className="h-4 w-4" />
            Generate drafts
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-[#9A8E78]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading calendar…
        </div>
      ) : isEmpty ? (
        <div
          data-testid="calendar-empty"
          className="rounded-2xl border border-[#5E594E] bg-[#5A5347] p-10 text-center"
        >
          <CalendarClock className="mx-auto h-8 w-8 text-[#C9A961]" />
          <p className="mt-4 font-serif text-2xl tracking-wide text-[#F5EFE2]">
            Your calendar is a blank workshop bench.
          </p>
          <p className="mt-1 text-sm text-[#C9BEA8]">Click below to start shaping it.</p>
          <Button
            onClick={() => setModalOpen(true)}
            data-testid="calendar-empty-cta"
            size="lg"
            className="mt-6 bg-[#C9A961] text-[#3D3A33] hover:bg-[#DDBE7A]"
          >
            <Sparkles className="h-4 w-4" />
            Generate your first 14 days of content · ~2 minutes · 84 AI generations
          </Button>
        </div>
      ) : (
        <div>
          {/* Month grid */}
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-2xl border border-[#5E594E] bg-[#5E594E]" data-testid="calendar-month-grid">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="bg-[#5A5347] px-2 py-2 text-center text-[10px] uppercase tracking-wider text-[#9A8E78]">
                {d}
              </div>
            ))}
            {monthMatrix.map((d, i) => {
              const key = ymdKey(d);
              const inMonth = d.getUTCMonth() === cursor.getUTCMonth();
              const cellPosts = grouped[key] || [];
              return (
                <div
                  key={i}
                  className={`min-h-[100px] bg-[#5A5347] p-1.5 ${inMonth ? "" : "opacity-40"}`}
                >
                  <div className="mb-1 text-[11px] text-[#C9BEA8]">
                    {d.getUTCDate()}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {cellPosts.slice(0, 6).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setDrawerPost(p)}
                        data-testid={`calendar-pill-${p.id}`}
                        title={`${p.platforms?.[0]} · ${p.status} · ${p.content?.slice(0, 60)}…`}
                        className={`rounded-full border px-1.5 py-0.5 text-[9px] uppercase tracking-wider transition-transform hover:scale-105 ${
                          STATUS_STYLES[p.status] || STATUS_STYLES.draft
                        }`}
                      >
                        {(p.platforms?.[0] || "??").slice(0, 2)}
                      </button>
                    ))}
                    {cellPosts.length > 6 && (
                      <span className="text-[9px] text-[#9A8E78]">+{cellPosts.length - 6}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-[#9A8E78]">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-4 rounded-full bg-[#5E594E]" />draft</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-4 rounded-full bg-[#7A8B6F]/40 border border-[#7A8B6F]/60" />scheduled</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-4 rounded-full bg-[#C9A961]/40 border border-[#C9A961]/70" />posted</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-4 rounded-full bg-[#B85A4A]/40 border border-[#B85A4A]/70" />failed</span>
            <span className="ml-auto">{posts.length} posts total</span>
          </div>
        </div>
      )}

      <GenerateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDone={() => { setModalOpen(false); load(); }}
      />
      <EditDrawer
        post={drawerPost}
        library={library}
        onClose={() => setDrawerPost(null)}
        onSaved={(updated) => {
          setPosts((cur) => cur.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
          setDrawerPost(null);
        }}
        onDeleted={(id) => {
          setPosts((cur) => cur.filter((p) => p.id !== id));
          setDrawerPost(null);
        }}
      />
    </div>
  );
}
